import React, { memo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styles from "./Nav.module.scss";
import { useDispatch } from "react-redux";
import { update_business_id } from "../../../features/business/businessSlice";
import { LOGIN_POST } from "../../../services/const";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Logo from './logo.png';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import MessageIcon from '@mui/icons-material/Message';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const LoginModal = memo(function LoginModal({ show, onClose, onSuccess }) {
  const [loginUserName, setLoginUserName] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!show) return null;

  const handleModalLogin = async (e) => {
    e.preventDefault();
    if (!loginUserName || !loginPassword) {
      setLoginError("Enter an email or username and password");
      return;
    }

    setIsLoggingIn(true);
    setLoginError("");
    try {
      const response = await fetch(new URL(LOGIN_POST), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: loginUserName,
          password: loginPassword
        })
      });
      const data = await response.json();

      if (data && data.code === 200 && data.content) {
        sessionStorage.setItem('bb_token', data.content.token);
        sessionStorage.setItem('bb_userId', data.content.userId);
        sessionStorage.setItem('bb_role', data.content.role);
        sessionStorage.setItem('bb_fullName', data.content.fullName);
        onSuccess(data.content.role);
      } else {
        setLoginError(data?.message || "Wrong password. Try again or click Forgot password.");
      }
    } catch (error) {
      setLoginError("Unable to connect to server. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleClose = () => {
    setLoginError("");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[2500] flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs transition-opacity animate-fade-in"
      onClick={handleClose}
      style={{ fontFamily: '"Google Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
    >
      <div
        className="w-full max-w-[440px] bg-white rounded-[8px] sm:rounded-[12px] border border-[#dadce0] shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)] p-7 sm:p-9 relative text-left"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: '"Google Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 text-[#5f6368] hover:text-[#202124] flex items-center justify-center transition-colors cursor-pointer"
          aria-label="Close dialog"
        >
          <i className="fa-solid fa-xmark text-sm"></i>
        </button>

        {/* Google Style Brand Header */}
        <div className="mb-7">
          <h2
            className="text-[24px] font-normal text-[#202124] tracking-normal leading-snug"
            style={{ fontFamily: '"Google Sans", "Roboto", sans-serif' }}
          >
            Sign in
          </h2>
          <p className="text-[14px] font-normal text-[#5f6368] mt-1.5 leading-relaxed">
            Access your business dashboard and state filings
          </p>
        </div>

        {/* Login Form */}
        <form className="space-y-4" onSubmit={handleModalLogin}>
          {/* Email / Username Field */}
          <div>
            <label className="block text-[13px] font-medium text-[#202124] mb-1.5">
              Username or email
            </label>
            <div className="relative">
              <input
                id="modal-login-username"
                type="text"
                value={loginUserName}
                onChange={(e) => setLoginUserName(e.target.value)}
                autoComplete="username"
                placeholder="Enter your username or email"
                className="w-full px-3.5 py-3 bg-white border border-[#dadce0] hover:border-[#202124] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] rounded-[4px] text-[15px] text-[#202124] placeholder:text-[#80868b] focus:outline-none transition-all"
                style={{ fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif' }}
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[13px] font-medium text-[#202124]">
                Password
              </label>
              <button
                type="button"
                className="text-[13px] font-medium text-[#1a73e8] hover:underline cursor-pointer"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                id="modal-login-password"
                type={showPassword ? "text" : "password"}
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
                className="w-full px-3.5 pr-11 py-3 bg-white border border-[#dadce0] hover:border-[#202124] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] rounded-[4px] text-[15px] text-[#202124] placeholder:text-[#80868b] focus:outline-none transition-all"
                style={{ fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif' }}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-[#5f6368] hover:text-[#202124] cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <VisibilityOff sx={{ fontSize: 19 }} />
                ) : (
                  <Visibility sx={{ fontSize: 19 }} />
                )}
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {loginError && (
            <div className="bg-[#fce8e6] border border-[#fad2cf] rounded-[4px] p-3 flex items-start gap-2 text-[#c5221f]">
              <i className="fa-solid fa-circle-exclamation text-xs mt-0.5 shrink-0"></i>
              <span className="text-[13px] font-normal">{loginError}</span>
            </div>
          )}

          {/* Action Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full inline-flex items-center justify-center gap-2 py-[11px] px-6 bg-[#1a73e8] hover:bg-[#1b66c9] active:bg-[#185abc] text-white font-medium text-[14px] rounded-[4px] shadow-none hover:shadow-xs transition-all duration-150 disabled:opacity-60 cursor-pointer tracking-[0.25px]"
              style={{ fontFamily: '"Google Sans", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif' }}
            >
              {isLoggingIn ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-sm"></i>
                  <span>Signing in...</span>
                </>
              ) : (
                <span>Sign in</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default function Nav() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const navmenu = [
    { url: "/", name: "Home" },
    {
      name: "Services",
      drpDown: [
        { id: 1, name: "LLC Formation", url: "/services/llc-formation" },
        { id: 2, name: "Corporation Formation", url: "/services/corporation-formation" },
        { name: "Compliance (BOI)", url: "/services/compliance" },
        { name: "Licensing and guidance", url: "/services/licensing" }
      ]
    },
    {
      name: "Resources",
      drpDown: [
        { name: "Step-by-step checklist", url: "/resources/step-by-step-checklist" },
        { name: "LLC vs. Corporation", url: "/resources/llc-vs-corporation" },
        { name: "S-Corparation vs. C-Corporation", url: "/resources/scorp-vs-ccorp" },
        { name: "What is an EIN?", url: "/resources/employer-id-number" },
        { name: "What is BOI?", url: "/resources/beneficial-Ownership-info" }
      ]
    },
    { url: "/about", name: "About Us" }
  ];

  const toggleMobileMenu = (e) => {
    if (e.target.innerText === "Contact Us" || e.target.innerText === "Login") {
      setShowMenu(false);
      return null;
    }

    if (e && (e.target.innerText === "Services" || e.target.innerText === "Resources")) {
      return;
    }
    setShowMenu(!showMenu);
    document.body.classList.toggle("no-scroll");
  };

  const toggleServices = (e) => {
    e.stopPropagation();
    setShowServices(!showServices);
  };

  const toggleResources = (e) => {
    e.stopPropagation();
    setShowResources(!showResources);
  };

  const handleSubItemClick = (id) => {
    console.log("Id: ", id);
    dispatch(update_business_id(id));
  };

  const openLoginModal = (e) => {
    if (e) e.preventDefault();
    setShowLoginModal(true);
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };
  const handleLoginSuccess = (role) => {
    setShowLoginModal(false);
    setShowMenu(false);
    navigate(role === 'admin' ? '/home' : '/table');
  };

  const isSubRouteActive = (item) =>
    Array.isArray(item?.drpDown) && item.drpDown.some((subItem) => location.pathname === subItem.url);

  const isTopRouteActive = (item) =>
    (item.url && location.pathname === item.url) || isSubRouteActive(item);

  return (
    <div className="font-sans antialiased">
      {/* Desktop & Mobile Sticky Header */}
      <header className="fixed top-0 left-0 w-full bg-white/95 backdrop-blur-md border-b border-[#dadce0] z-[100] shadow-sm transition-all duration-300">
        <div className="container mx-auto px-4 lg:px-8">
          <nav className="flex items-center justify-between h-[70px] lg:h-[80px]">
            
            {/* Left Side: Logo & Main Navigation */}
            <div className="flex items-center gap-6 lg:gap-8">
              {/* Logo */}
              <Link to="/" className="block flex-shrink-0">
                <img src={Logo} alt="Logo" className="h-[36px] lg:h-[42px] w-auto object-contain" />
              </Link>
              
              {/* Desktop Nav Links */}
              <ul className="hidden lg:flex items-center gap-1 lg:gap-2">
                {navmenu.map((item) => (
                  <li key={item.name} className="relative group">
                    {item.name === "Services" || item.name === "Resources" ? (
                      <div className={`px-3 py-2 rounded-[4px] cursor-pointer flex items-center gap-1 text-[15px] font-medium transition-colors ${isTopRouteActive(item) ? 'text-[#1a73e8]' : 'text-[#3c4043] hover:text-[#1a73e8] hover:bg-[#f1f3f4]'}`}>
                        {item.name} 
                        <ExpandMoreIcon className="text-[18px] transition-transform duration-300 group-hover:-rotate-180" />
                      </div>
                    ) : (
                      <Link 
                        to={item.url} 
                        className={`block px-3 py-2 rounded-[4px] text-[15px] font-medium transition-colors ${isTopRouteActive(item) ? 'text-[#1a73e8]' : 'text-[#3c4043] hover:text-[#1a73e8] hover:bg-[#f1f3f4]'}`}
                      >
                        {item.name}
                      </Link>
                    )}

                    {/* Dropdown Menu (Tailwind Group Hover) */}
                    {item.drpDown && (
                      <div className="hidden group-hover:block absolute top-full left-0 pt-2 z-50">
                        <ul className="w-max min-w-[240px] bg-white border border-[#dadce0] rounded-[8px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-2 flex flex-col gap-0.5 relative before:absolute before:top-[-6px] before:left-8 before:w-3 before:h-3 before:bg-white before:border-t before:border-l before:border-[#dadce0] before:rotate-45">
                          {item.drpDown.map((subItem, index) => (
                            <li key={index} className="w-full">
                              <Link 
                                to={subItem.url} 
                                className={`block w-full px-5 py-2.5 rounded-[6px] text-[15px] whitespace-nowrap transition-all duration-200 ${location.pathname === subItem.url ? 'bg-[#e8f0fe] text-[#1a73e8] font-medium' : 'text-[#3c4043] hover:bg-[#f8f9fa] hover:text-[#202124]'}`}
                                onClick={() => handleSubItemClick(subItem.id)}
                              >
                                {subItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Actions (Desktop) */}
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/contact-us" className="flex items-center gap-2 text-[#5f6368] hover:text-[#1a73e8] text-[15px] font-medium transition-colors">
                <i className="fa-solid fa-headset text-[18px]"></i>
                <span>Customer Support</span>
              </Link>
              
              <button 
                type="button" 
                onClick={openLoginModal}
                className="bg-[#1a73e8] text-white hover:bg-[#1b66c9] px-6 py-2.5 rounded-[4px] flex items-center gap-2 text-[15px] font-medium transition-colors border border-transparent"
              >
                <AccountCircleIcon className="text-[20px]" />
                <span>Login</span>
              </button>
            </div>

            {/* Mobile Menu Toggle Button */}
            <div className="flex lg:hidden items-center gap-4">
              <button 
                type="button" 
                onClick={openLoginModal}
                className="text-[#1a73e8] flex items-center justify-center p-2 rounded-full hover:bg-[#e8f0fe] transition-colors"
              >
                <AccountCircleIcon className="text-[24px]" />
              </button>
              <button 
                onClick={toggleMobileMenu} 
                className="text-[#3c4043] hover:text-[#1a73e8] p-2 focus:outline-none flex flex-col justify-center items-center w-10 h-10 rounded-full hover:bg-[#f1f3f4] transition-colors"
              >
                <span className={`block w-6 h-[2px] bg-current transition-all duration-300 ${showMenu ? 'rotate-45 translate-y-[6px]' : '-translate-y-1'}`}></span>
                <span className={`block w-6 h-[2px] bg-current transition-all duration-300 ${showMenu ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block w-6 h-[2px] bg-current transition-all duration-300 ${showMenu ? '-rotate-45 -translate-y-[6px]' : 'translate-y-1'}`}></span>
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Right Drawer */}
      <div className={`fixed inset-0 bg-black/50 z-[110] lg:hidden transition-opacity duration-300 ${showMenu ? 'opacity-100 visible' : 'opacity-0 invisible'}`} onClick={toggleMobileMenu}>
        <div 
          className={`absolute top-0 right-0 w-[80%] max-w-[320px] h-full bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.15)] flex flex-col overflow-y-auto transition-transform duration-300 ease-in-out ${showMenu ? 'translate-x-0' : 'translate-x-full'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#dadce0]">
            <img src={Logo} alt="Logo" className="h-[32px] w-auto" />
            <button onClick={toggleMobileMenu} className="text-[#5f6368] hover:text-[#1a73e8] p-2 bg-[#f1f3f4] rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
            </button>
          </div>

          <ul className="flex flex-col py-3">
            {navmenu.map((item) => (
              <li key={item.name} className="flex flex-col mb-1">
                {item.name === "Services" || item.name === "Resources" ? (
                  <div className="flex flex-col">
                    <button 
                      className={`flex items-center justify-between w-full px-4 py-3 mx-3 text-[15px] font-medium rounded-[8px] transition-colors ${isTopRouteActive(item) ? 'text-[#1a73e8] bg-[#e8f0fe]' : 'text-[#3c4043] hover:bg-[#f1f3f4]'}`}
                      style={{ width: 'calc(100% - 24px)' }}
                      onClick={item.name === "Services" ? toggleServices : toggleResources}
                    >
                      <span>{item.name}</span>
                      {item.name === "Services" && (showServices ? <ExpandLessIcon className="text-[#5f6368]" /> : <ExpandMoreIcon className="text-[#5f6368]" />)}
                      {item.name === "Resources" && (showResources ? <ExpandLessIcon className="text-[#5f6368]" /> : <ExpandMoreIcon className="text-[#5f6368]" />)}
                    </button>
                    {/* Mobile Submenu Dropdown */}
                    <div className={`overflow-hidden transition-all duration-300 ${((item.name === "Services" && showServices) || (item.name === "Resources" && showResources)) ? 'max-h-[400px] mt-1' : 'max-h-0'}`}>
                      <ul className="flex flex-col pl-4">
                        {item.drpDown.map((subItem, index) => (
                          <li key={index} className="mb-1">
                            <Link 
                              to={subItem.url} 
                              className={`block px-4 py-2.5 mx-3 text-[14px] rounded-[8px] transition-colors ${location.pathname === subItem.url ? 'text-[#1a73e8] bg-[#e8f0fe]' : 'text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#1a73e8]'}`}
                              style={{ width: 'calc(100% - 24px)' }}
                              onClick={(e) => { handleSubItemClick(subItem.id); toggleMobileMenu(e); }}
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <Link 
                    to={item.url} 
                    className={`block px-4 py-3 mx-3 text-[15px] font-medium rounded-[8px] transition-colors ${isTopRouteActive(item) ? 'text-[#1a73e8] bg-[#e8f0fe]' : 'text-[#3c4043] hover:bg-[#f1f3f4]'}`}
                    style={{ width: 'calc(100% - 24px)' }}
                    onClick={(e) => toggleMobileMenu(e)}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          
          <div className="mt-auto p-6 flex flex-col gap-4 border-t border-[#dadce0] bg-[#f8f9fa]">
            <Link to="/contact-us" className="flex items-center justify-center gap-2 text-[#5f6368] border border-[#dadce0] bg-white py-3 rounded-[4px] font-medium text-[15px]" onClick={toggleMobileMenu}>
              <i className="fa-solid fa-headset"></i> Customer Support
            </Link>
            <button type="button" onClick={() => { setShowMenu(false); openLoginModal(); }} className="flex items-center justify-center gap-2 bg-[#1a73e8] text-white py-3 rounded-[4px] font-medium text-[15px]">
              <AccountCircleIcon /> Login / Dashboard
            </button>
          </div>
        </div>
      </div>

      <LoginModal
        show={showLoginModal}
        onClose={closeLoginModal}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
}


